import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { CheckCircle, Lock } from 'phosphor-react';

const StripePaymentElement = ({ orderData, onPaymentSuccess, onPaymentError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment intent on your backend first
      const response = await fetch('https://hkdysxiyfpqsnxtexqcm.supabase.co/functions/v1/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: orderData.total_amount, // Keep as euros
          currency: orderData.currency.toLowerCase(),
          order_id: orderData.order_number,
          metadata: {
            user_id: orderData.user_id,
            user_email: orderData.user_email,
          }
        })
      });

      const { client_secret } = await response.json();

      // Confirm payment with Payment Element
      const { error: paymentError } = await stripe.confirmPayment({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          payment_method_data: {
            billing_details: {
              name: `${orderData.shipping_address?.first_name || ''} ${orderData.shipping_address?.last_name || ''}`.trim(),
              email: orderData.user_email,
              address: {
                line1: orderData.shipping_address?.address_line_1,
                city: orderData.shipping_address?.city,
                postal_code: orderData.shipping_address?.postal_code,
                country: 'BE',
              },
            },
          },
        },
        redirect: 'if_required', // Handle without redirect for SPA
      });

      if (paymentError) {
        setError(paymentError.message);
        onPaymentError(paymentError);
      } else {
        // Payment successful
        onPaymentSuccess({
          paymentMethod: { type: 'payment_element' },
          paymentId: client_secret,
          amount: orderData.total_amount * 100,
        });
      }

    } catch (err) {
      setError('An error occurred during payment processing.');
      onPaymentError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-8">
      <div className="flex items-center justify-center mb-6">
        <Lock className="w-6 h-6 text-green-600 mr-2" />
        <h2 className="text-2xl font-semibold text-gray-800">Secure Payment</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Payment Element - supports cards, wallets, bank transfers, etc. */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <PaymentElement
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
              fields: {
                billingDetails: {
                  name: 'auto',
                  email: 'auto',
                  phone: 'auto',
                  address: 'auto',
                }
              }
            }}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={!stripe || loading}
            className="flex-1 bg-gradient-to-r from-ambre-600 to-ambre-700 text-white px-4 py-3 rounded-lg hover:from-ambre-700 hover:to-ambre-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Pay €{orderData.total_amount.toFixed(2)}</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Order Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium text-gray-800 mb-2">Order Summary</h3>
        <div className="space-y-1 text-sm text-gray-600">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>€{orderData.total_amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Shipping</span>
            <span>Free</span>
          </div>
          <div className="flex justify-between font-medium text-gray-800 border-t border-gray-200 pt-1">
            <span>Total</span>
            <span>€{orderData.total_amount.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripePaymentElement; 